import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoJsonLd from '../../components/feature/SeoJsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const commanderPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Commander – Livraison locale rapide | Localygo',
  url: `${SITE_URL}/commander`,
  description: 'Commandez auprès de vos restaurants et boutiques locaux. Livraison en moins de 40 minutes par un habitant de votre quartier. Paiement sécurisé, suivi en temps réel.',
  provider: {
    '@type': 'Organization',
    name: 'Localygo',
    url: SITE_URL,
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Quels sont les délais de livraison ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'En moyenne 25 à 40 minutes selon votre distance et le commerce choisi. Le délai estimé est affiché avant de passer commande.',
      },
    },
    {
      '@type': 'Question',
      name: 'Comment suivre ma commande ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Après validation, vous recevez un lien de suivi par SMS et email. Vous pouvez suivre votre livreur en temps réel sur la carte.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quels modes de paiement sont acceptés ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Carte bancaire (Visa, Mastercard), Apple Pay, Google Pay et paiement en espèces à la livraison.',
      },
    },
    {
      '@type': 'Question',
      name: 'Puis-je annuler ma commande ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Vous pouvez annuler gratuitement dans les 2 minutes suivant la commande. Passé ce délai, contactez notre support.',
      },
    },
    {
      '@type': 'Question',
      name: 'Y a-t-il un minimum de commande ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Le minimum varie selon le commerce, généralement entre 8 € et 15 €. Il est indiqué sur la page du restaurant ou de la boutique.',
      },
    },
    {
      '@type': 'Question',
      name: 'Comment fonctionne le programme fidélité ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Vous gagnez 1 point par euro dépensé. 100 points = 5 € de réduction sur votre prochaine commande.',
      },
    },
  ],
};

const STEPS = [
  {
    num: '01',
    icon: 'ri-map-pin-2-line',
    title: 'Entrez votre adresse',
    desc: 'Indiquez votre adresse de livraison pour voir les restaurants et boutiques disponibles près de chez vous.',
  },
  {
    num: '02',
    icon: 'ri-restaurant-line',
    title: 'Choisissez votre commerce',
    desc: 'Parcourez les restaurants et boutiques locaux, filtrez par cuisine, note ou distance.',
  },
  {
    num: '03',
    icon: 'ri-shopping-basket-2-line',
    title: 'Composez votre panier',
    desc: 'Ajoutez vos plats ou produits préférés, personnalisez votre commande selon vos envies.',
  },
  {
    num: '04',
    icon: 'ri-bank-card-line',
    title: 'Payez en toute sécurité',
    desc: 'Réglez en ligne par carte bancaire ou en espèces à la livraison. Paiement 100% sécurisé.',
  },
  {
    num: '05',
    icon: 'ri-map-2-line',
    title: 'Suivez en temps réel',
    desc: 'Suivez votre livreur sur la carte en direct. Recevez des notifications à chaque étape.',
  },
];

const FEATURES = [
  { icon: 'ri-timer-flash-line', title: 'Livraison express', desc: 'En moyenne 30 minutes pour vos commandes dans votre quartier.' },
  { icon: 'ri-shield-check-line', title: 'Paiement sécurisé', desc: 'Vos données bancaires sont protégées par un chiffrement SSL.' },
  { icon: 'ri-customer-service-2-line', title: 'Support 7j/7', desc: 'Notre équipe est disponible tous les jours pour vous aider.' },
  { icon: 'ri-leaf-line', title: 'Commerce local', desc: 'Chaque commande soutient directement les commerçants de votre ville.' },
  { icon: 'ri-gift-2-line', title: 'Programme fidélité', desc: 'Cumulez des points à chaque commande et obtenez des réductions.' },
  { icon: 'ri-notification-3-line', title: 'Suivi en direct', desc: 'Notifications en temps réel à chaque étape de votre livraison.' },
];

const POPULAR_ITEMS = [
  {
    id: 1,
    name: 'Thiéboudienne',
    restaurant: 'Chez Fatou',
    price: '12,90 €',
    rating: 4.9,
    image: 'https://public.readdy.ai/ai/img_res/6c7ba1b2875f4160658269f8b0a696bb.jpg',
  },
  {
    id: 2,
    name: 'Pizza Margherita',
    restaurant: 'Bella Napoli',
    price: '11,50 €',
    rating: 4.7,
    image: 'https://readdy.ai/api/search-image?query=classic%20italian%20margherita%20pizza%20fresh%20mozzarella%20basil%20tomato%20sauce%20wood%20fired%20crispy%20crust%20beautiful%20food%20photography%20clean%20white%20background%20appetizing%20presentation&width=400&height=300&seq=cmd002&orientation=landscape',
  },
  {
    id: 3,
    name: 'Sushi Mix 12 pièces',
    restaurant: 'Tokyo Sushi Bar',
    price: '18,90 €',
    rating: 4.9,
    image: 'https://readdy.ai/api/search-image?query=japanese%20sushi%20platter%20assorted%20nigiri%20maki%20rolls%20fresh%20fish%20colorful%20presentation%20elegant%20wooden%20board%20clean%20white%20background%20professional%20food%20photography&width=400&height=300&seq=cmd003&orientation=landscape',
  },
  {
    id: 4,
    name: 'Burger Gourmet',
    restaurant: 'Le Burger Gourmet',
    price: '13,50 €',
    rating: 4.6,
    image: 'https://readdy.ai/api/search-image?query=gourmet%20artisan%20burger%20juicy%20beef%20patty%20fresh%20vegetables%20brioche%20bun%20melted%20cheese%20beautiful%20food%20photography%20clean%20white%20background%20appetizing%20presentation&width=400&height=300&seq=cmd004&orientation=landscape',
  },
  {
    id: 5,
    name: 'Poulet Braisé',
    restaurant: "Le Maquis d'Abidjan",
    price: '14,00 €',
    rating: 4.8,
    image: 'https://readdy.ai/api/search-image?query=ivorian%20grilled%20chicken%20braise%20with%20atti%C3%A9k%C3%A9%20alloco%20traditional%20west%20african%20dish%20colorful%20plate%20warm%20tones%20appetizing%20food%20photography%20clean%20simple%20background&width=400&height=300&seq=cmd005&orientation=landscape',
  },
  {
    id: 6,
    name: 'Buddha Bowl',
    restaurant: 'Le Jardin Vert',
    price: '10,90 €',
    rating: 4.8,
    image: 'https://readdy.ai/api/search-image?query=colorful%20healthy%20buddha%20bowl%20with%20quinoa%20avocado%20roasted%20vegetables%20chickpeas%20fresh%20greens%20beautiful%20presentation%20clean%20white%20background%20professional%20food%20photography&width=400&height=300&seq=cmd006&orientation=landscape',
  },
];

const FAQS = [
  { q: 'Quels sont les délais de livraison ?', a: 'En moyenne 25 à 40 minutes selon votre distance et le commerce choisi. Le délai estimé est affiché avant de passer commande.' },
  { q: 'Comment suivre ma commande ?', a: 'Après validation, vous recevez un lien de suivi par SMS et email. Vous pouvez suivre votre livreur en temps réel sur la carte.' },
  { q: 'Quels modes de paiement sont acceptés ?', a: 'Carte bancaire (Visa, Mastercard), Apple Pay, Google Pay et paiement en espèces à la livraison.' },
  { q: 'Puis-je annuler ma commande ?', a: 'Vous pouvez annuler gratuitement dans les 2 minutes suivant la commande. Passé ce délai, contactez notre support.' },
  { q: 'Y a-t-il un minimum de commande ?', a: 'Le minimum varie selon le commerce, généralement entre 8 € et 15 €. Il est indiqué sur la page du restaurant ou de la boutique.' },
  { q: 'Comment fonctionne le programme fidélité ?', a: 'Vous gagnez 1 point par euro dépensé. 100 points = 5 € de réduction sur votre prochaine commande.' },
];

export default function CommanderPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [address, setAddress] = useState('');

  return (
    <div className="min-h-screen bg-white">
      <SeoJsonLd data={commanderPageSchema} id="schema-commander" />
      <SeoJsonLd data={faqSchema} id="schema-commander-faq" />
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[580px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=food%20delivery%20app%20interface%20on%20smartphone%20with%20diverse%20delicious%20meals%20in%20background%20warm%20inviting%20atmosphere%20modern%20technology%20meets%20local%20food%20culture%20vibrant%20colors%20appetizing%20dishes%20french%20city&width=1440&height=700&seq=commander-hero&orientation=landscape"
          alt="Commander sur Localygo"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20"></div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <span className="inline-block bg-teal-500/20 border border-teal-400/40 text-teal-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-6 uppercase tracking-widest">
              Livraison locale rapide
            </span>
            <h1 className="text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Commandez local,<br />
              <span className="text-teal-400">livré vite</span>
            </h1>
            <p className="text-lg text-white/80 mb-8 leading-relaxed">
              Restaurants, épiceries, boulangeries… tout ce dont vous avez besoin, livré en moins de 40 minutes par un habitant de votre quartier.
            </p>
            {/* Barre de recherche adresse */}
            <div className="flex gap-3 max-w-xl">
              <div className="flex-1 relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-line text-teal-500 text-lg"></i>
                </div>
                <input
                  type="text"
                  placeholder="Entrez votre adresse..."
                  value={address}
                  onChange={e => setAddress(e.target.value)}
                  className="w-full pl-11 pr-4 py-4 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 shadow-lg"
                />
              </div>
              <Link
                to="/restaurants"
                className="bg-teal-500 hover:bg-teal-400 text-white px-6 py-4 rounded-xl font-semibold text-sm transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                Voir les commerces
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-teal-600 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
            {[
              { value: '50+', label: 'Commerces partenaires' },
              { value: '30 min', label: 'Délai moyen' },
              { value: '10 000+', label: 'Commandes livrées' },
              { value: '4.8★', label: 'Note moyenne' },
            ].map(stat => (
              <div key={stat.label}>
                <div className="text-3xl font-extrabold mb-1">{stat.value}</div>
                <div className="text-sm text-teal-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Comment commander ?</h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">Simple, rapide et local. En 5 étapes, votre commande est en route.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {STEPS.map((step, i) => (
              <div key={step.num} className="relative text-center">
                {i < STEPS.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-teal-100 z-0" style={{ width: 'calc(100% - 2rem)', left: 'calc(50% + 2rem)' }}></div>
                )}
                <div className="relative z-10">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white rounded-full mx-auto mb-4 shadow-lg">
                    <i className={`${step.icon} text-2xl`}></i>
                  </div>
                  <span className="text-xs font-bold text-teal-500 uppercase tracking-widest mb-2 block">{step.num}</span>
                  <h3 className="text-sm font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/restaurants"
              className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-10 py-4 rounded-full font-bold text-base transition-all shadow-lg cursor-pointer whitespace-nowrap"
            >
              Commencer à commander
            </Link>
          </div>
        </div>
      </section>

      {/* Plats populaires */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Les plus commandés</h2>
              <p className="text-gray-500">Les plats préférés de nos clients cette semaine</p>
            </div>
            <Link to="/restaurants" className="text-teal-600 hover:text-teal-700 font-semibold text-sm flex items-center gap-1 cursor-pointer whitespace-nowrap">
              Voir tout <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {POPULAR_ITEMS.map(item => (
              <div key={item.id} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all group cursor-pointer">
                <div className="h-44 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-bold text-gray-900 text-sm">{item.name}</h3>
                    <span className="text-teal-600 font-bold text-sm whitespace-nowrap ml-2">{item.price}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{item.restaurant}</p>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-amber-400 text-xs"></i>
                    <span className="text-xs font-semibold text-gray-700">{item.rating}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fonctionnalités */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Pourquoi choisir Localygo ?</h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">Une expérience de livraison pensée pour vous et votre quartier.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {FEATURES.map(f => (
              <div key={f.title} className="flex gap-4 p-6 bg-gray-50 rounded-xl hover:shadow-sm transition-all">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl shrink-0">
                  <i className={`${f.icon} text-xl text-teal-600`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{f.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Questions fréquentes</h2>
            <p className="text-gray-500 text-base">Tout ce que vous devez savoir avant de commander.</p>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 text-sm">{faq.q}</span>
                  <div className="w-5 h-5 flex items-center justify-center shrink-0 ml-4">
                    <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-teal-600 text-lg`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-50">
                    <p className="pt-3">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-teal-700 text-white text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-4xl font-extrabold mb-4">Prêt à commander ?</h2>
          <p className="text-teal-100 text-base mb-8">Découvrez tous les restaurants et boutiques disponibles près de chez vous.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/restaurants"
              className="bg-white text-teal-700 font-bold px-8 py-4 rounded-full hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Voir les restaurants
            </Link>
            <Link
              to="/boutiques"
              className="border-2 border-white text-white font-bold px-8 py-4 rounded-full hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              Voir les boutiques
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
